import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listingService } from '../services';
import MapView from '../components/MapView';

// Keep track of failed image URLs globally to prevent repeated retries
// Reset on component load to give images another chance on page refresh
// eslint-disable-next-line no-unused-vars
const failedImageUrls = new Set();

function ListingsPage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    foodType: '',
    expiryDate: '',
    distance: '',
    location: '',
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  
  const { user } = useAuth();

  // Fetch listings when component mounts
  useEffect(() => {
    fetchListings();
  }, []);

  // Fetch listings from the API
  const fetchListings = async () => {
    setLoading(true);
    try {
      // Use the listingService to fetch listings
      const response = await listingService.getAllListings();
      console.log('API response:', response);
      
      // Extract listings based on the known response structure
      // The actual listings array is in response.listings.results
      if (response?.listings?.results && Array.isArray(response.listings.results)) {
        console.log('Found listings in response.listings.results');
        setListings(response.listings.results);
      } else if (Array.isArray(response?.listings)) {
        // Fallback to direct listings array if the structure changes
        setListings(response.listings);
      } else if (Array.isArray(response)) {
        // If response itself is the array
        setListings(response);
      } else {
        console.error('Could not find listings array in response:', response);
        setListings([]);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to fetch listings. Please try again later.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Format expiry date for display
  const formatExpiryDate = (dateString) => {
    // The API provides dates in format "2025-06-06 06:01:21"
    try {
      const date = new Date(dateString);
      if (!isNaN(date)) {
        return date.toLocaleDateString();
      }
      // Fallback parsing in case the date format isn't recognized
      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day).toLocaleDateString();
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateString || 'Unknown date';
    }
  };

  // Calculate if expiry date is close (within 24 hours)
  const isExpiryClose = (dateString) => {
    try {
      const expiryDate = new Date(dateString);
      const now = new Date();
      
      if (!isNaN(expiryDate)) {
        const diffHours = (expiryDate - now) / (1000 * 60 * 60);
        return diffHours >= 0 && diffHours <= 24;
      }
      
      // Fallback parsing if standard date parsing doesn't work
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      const [hour, minute, second] = timePart ? timePart.split(':').map(num => parseInt(num, 10)) : [0, 0, 0];
      
      const expiryDateFallback = new Date(year, month - 1, day, hour, minute, second);
      const diffHours = (expiryDateFallback - now) / (1000 * 60 * 60);
      return diffHours >= 0 && diffHours <= 24;
    } catch (err) {
      console.error('Error checking expiry:', err);
      return false;
    }
  };
  
  // Helper function to consistently get a valid image URL from listing data
  const getListingImageUrl = (listing) => {
    if (!listing) return '/images/placeholder-food.png';
    
    // Debug the image data structure
    console.log(`DEBUG - Listing ${listing.id} image data:`, { 
      imageUrl: listing.imageUrl, 
      images: listing.images,
      imageType: listing.images ? typeof listing.images : 'undefined'
    });
    
    // Helper function to only convert http to https without rewriting domains
    const fixImageUrl = (url) => {
      if (!url) return '/images/placeholder-food.png';
      
      // Test URL for example.com that may be seed data
      if (url.includes('example.com')) {
        console.log('Detected example.com URL, using placeholder');
        return '/images/placeholder-food.png';
      }
      
      // Convert HTTP to HTTPS for secure loading, but don't rewrite domains
      if (url.startsWith('http:')) {
        url = url.replace('http:', 'https:');
      }
      
      // Just use relative URLs as is
      if (url.startsWith('/api/images/')) {
        return `${window.location.origin}${url}`;
      }
      
      // Return the URL directly without domain rewriting
      return url;
    };
    
    // Check for imageUrl field first (this might be set by the backend)
    if (listing.imageUrl) {
      return fixImageUrl(listing.imageUrl);
    }
    
    // Handle case where images field is present
    if (listing.images) {
      // If images is a JSON string, parse it
      if (typeof listing.images === 'string') {
        try {
          // Direct string URL (not in array format)
          if (!listing.images.startsWith('[') && 
              (listing.images.startsWith('http') || 
               listing.images.includes('wastenot-api') ||
               listing.images.includes('wastenot-storage'))) {
            return fixImageUrl(listing.images);
          }
            
          if (listing.images.startsWith('[')) {
            // Try to parse as JSON array
            const imageArray = JSON.parse(listing.images);
            if (Array.isArray(imageArray) && imageArray.length > 0) {
              const imageUrl = imageArray[0];
              return fixImageUrl(imageUrl);
            }
          } else if (listing.images.includes('/api/images/') || 
                     listing.images.includes('wastenot-api.tinkacode.workers.dev')) {
            return fixImageUrl(listing.images);
          } else if (listing.images.startsWith('listings/')) {
            return fixImageUrl(listing.images);
          }
        } catch (e) {
          console.log(`Image parse error for listing ${listing.id}:`, e);
          // If parsing fails, just use the string directly
          if (listing.images.includes('/api/images/') || 
              listing.images.includes('wastenot-api.tinkacode.workers.dev')) {
            return fixImageUrl(listing.images);
          }
          if (listing.images.includes('wastenot-storage.wastenot.org')) {
            return fixImageUrl(listing.images);
          }
          if (listing.images.startsWith('listings/')) {
            return fixImageUrl(listing.images);
          }
        }
      }
      
      // If images is already an array
      if (Array.isArray(listing.images) && listing.images.length > 0) {
        const imageUrl = listing.images[0];
        return fixImageUrl(imageUrl);
      }
    }
    
    // Fallback to placeholder
    return '/images/placeholder-food.png';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Food Listings</h1>
          <p className="text-gray-600 mt-1">Browse available food donations in your area</p>
        </div>
        <div className="flex justify-end mb-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 text-sm rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm font-medium text-gray-800' : 'text-gray-600'}`}
            >
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Grid View
              </span>
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1 text-sm rounded-md ${viewMode === 'map' ? 'bg-white shadow-sm font-medium text-gray-800' : 'text-gray-600'}`}
            >
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map View
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="distance" className="block text-sm font-medium text-gray-700 mb-1">
              Distance
            </label>
            <select
              id="distance"
              name="distance"
              value={filters.distance}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">Any Distance</option>
              <option value="1">Within 1 km</option>
              <option value="5">Within 5 km</option>
              <option value="10">Within 10 km</option>
              <option value="25">Within 25 km</option>
            </select>
          </div>
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
              placeholder="Area or postal code"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Map View (placeholder for now) */}
      {/* Map View is now implemented with the MapView component */}

      {/* Listings */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No listings found</h3>
          <p className="mt-1 text-sm text-gray-500">Try changing your search filters.</p>
        </div>
      ) : viewMode === 'map' ? (
        <div className="mt-4">
          <MapView 
            listings={listings.map(listing => {
              // Parse location from listing data
              let location = null;
              
              try {
                // Try to parse location from the JSON string if it exists
                if (listing.location && typeof listing.location === 'string') {
                  try {
                    location = JSON.parse(listing.location);
                  } catch (e) {
                    console.error('Error parsing location string:', e);
                  }
                } else if (listing.location && typeof listing.location === 'object') {
                  location = listing.location;
                } 
                
                // Check for separate latitude and longitude fields
                if ((!location || !location.lat || !location.lng) && listing.latitude && listing.longitude) {
                  location = { lat: parseFloat(listing.latitude), lng: parseFloat(listing.longitude) };
                }
                
                // Check for alternative field names
                if ((!location || !location.lat || !location.lng) && listing.lat && listing.lng) {
                  location = { lat: parseFloat(listing.lat), lng: parseFloat(listing.lng) };
                }
                
                // Check for city or address information to get approximate location
                if (!location || !location.lat || !location.lng) {
                  // Extract city from listing
                  // Address might be used in future geolocation implementation
                  // eslint-disable-next-line no-unused-vars
                  const address = listing.address || '';
                  const city = listing.city || '';
                  
                  // Assign city-based coordinates as a fallback
                  if (city.toLowerCase().includes('nairobi')) {
                    location = { lat: -1.2921, lng: 36.8219 }; // Nairobi coordinates
                  } else if (city.toLowerCase().includes('mombasa')) {
                    location = { lat: -4.0435, lng: 39.6682 }; // Mombasa coordinates
                  } else if (city.toLowerCase().includes('kisumu')) {
                    location = { lat: -0.1022, lng: 34.7617 }; // Kisumu coordinates
                  } else {
                    // Default to central Kenya if no location found
                    // Add small random offset to prevent markers overlapping
                    const randomLat = (Math.random() - 0.5) * 0.02;
                    const randomLng = (Math.random() - 0.5) * 0.02;
                    location = { lat: -1.2921 + randomLat, lng: 36.8219 + randomLng };
                  }
                }
              } catch (e) {
                console.error('Error processing location:', e);
                // Fallback location with slight randomization to prevent overlapping markers
                const randomLat = (Math.random() - 0.5) * 0.01;
                const randomLng = (Math.random() - 0.5) * 0.01;
                location = { lat: -1.2921 + randomLat, lng: 36.8219 + randomLng }; // Default to Nairobi
              }
              
              // Return listing with parsed location
              return {
                ...listing,
                location: location,
                foodType: listing.category || listing.food_type || 'Food',
                expiryDate: listing.expiry_date || listing.expiry || new Date().toISOString(),
                // Add other fields needed by MapView
              };
            })}
            height="600px"
            /* Removed automatic redirection on marker click to allow popup to display */
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <div key={listing.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="relative">
                <img 
                  src={getListingImageUrl(listing)}
                  alt={listing.title} 
                  className="w-full h-48 object-cover"
                  crossOrigin="anonymous" 
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  onError={(e) => {
                    console.log(`Image load error for listing ${listing.id}:`, {
                      src: e.target.src,
                      listing: listing.id
                    });
                    
                    // Track failed URLs to prevent repeated retries in the future
                    failedImageUrls.add(e.target.src);
                    
                    e.target.onerror = null; // Prevent infinite loops
                    e.target.src = '/images/placeholder-food.png';
                  }}
                />
                {isExpiryClose(listing.expiry_date) && (
                  <div className="absolute top-2 right-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded-md font-medium">
                    Expires Soon
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900">{listing.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{listing.donor_name} • {listing.address}</p>
                <div className="mt-2 flex items-center">
                  <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-md font-medium">
                    {listing.category}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    Expires: {formatExpiryDate(listing.expiry_date)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500 line-clamp-2">{listing.description}</p>
                <div className="mt-4 flex justify-between items-center">
                  <Link
                    to={`/listings/${listing.id}`}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View Details
                  </Link>
                  {user && user.role === 'recipient' && (
                    <button
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Request
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ListingsPage;
